'use client'

import { cn } from '@mdxui/primitives/lib/utils'
import { useCallback, useEffect, useRef, useState } from 'react'

interface Heading {
  id: string
  text: string
  level: number
}

export interface TableOfContentsAuthor {
  name: string
  image?: string
}

export interface TableOfContentsProps {
  /** Heading selectors to include (default: 'h1, h2') */
  selector?: string
  /** Additional className */
  className?: string
  /** Author information to display below TOC */
  author?: TableOfContentsAuthor
}

export function TableOfContents({
  selector = 'h1, h2',
  className,
  author,
}: TableOfContentsProps) {
  const [headings, setHeadings] = useState<Heading[]>([])
  const [activeId, setActiveId] = useState<string>('')
  const observerRef = useRef<IntersectionObserver | null>(null)
  const headingElementsRef = useRef<Map<string, IntersectionObserverEntry>>(new Map())
  // Track activeId in a ref for the observer callback to avoid stale closure issues
  const activeIdRef = useRef(activeId)

  // Query and cache headings from the DOM
  const queryHeadings = useCallback(() => {
    const headingElements = document.querySelectorAll(selector)
    const headingsArray: Heading[] = []

    headingElements.forEach((element) => {
      if (element.id) {
        headingsArray.push({
          id: element.id,
          text: element.textContent || '',
          level: Number.parseInt(element.tagName.charAt(1)),
        })
      }
    })

    return headingsArray
  }, [selector])

  // Effect to detect headings with retry for timing issues
  useEffect(() => {
    // Try to find headings immediately
    let foundHeadings = queryHeadings()

    if (foundHeadings.length > 0) {
      setHeadings(foundHeadings)
      // Set first heading as active by default
      if (foundHeadings[0] && !activeId) {
        setActiveId(foundHeadings[0].id)
      }
      return
    }

    // If no headings found, retry after a short delay (content may not be rendered yet)
    const retryTimeouts = [50, 150, 300, 500]
    const timeoutIds: ReturnType<typeof setTimeout>[] = []

    retryTimeouts.forEach((delay) => {
      const timeoutId = setTimeout(() => {
        foundHeadings = queryHeadings()
        if (foundHeadings.length > 0) {
          setHeadings(foundHeadings)
          // Set first heading as active by default
          if (foundHeadings[0] && !activeId) {
            setActiveId(foundHeadings[0].id)
          }
          // Clear remaining timeouts
          timeoutIds.forEach(clearTimeout)
        }
      }, delay)
      timeoutIds.push(timeoutId)
    })

    return () => {
      timeoutIds.forEach(clearTimeout)
    }
  }, [queryHeadings, activeId])

  // Keep ref in sync with state
  useEffect(() => {
    activeIdRef.current = activeId
  }, [activeId])

  // Use IntersectionObserver for reliable scroll detection
  // This works regardless of whether scrolling happens on window or a container
  useEffect(() => {
    if (headings.length === 0) return

    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect()
    }
    headingElementsRef.current.clear()

    const callback: IntersectionObserverCallback = (entries) => {
      // Update the map with latest intersection data
      entries.forEach((entry) => {
        headingElementsRef.current.set(entry.target.id, entry)
      })

      // Find the heading that should be active
      // Strategy: Find the topmost visible heading, or if none visible, the last one that scrolled past
      const visibleHeadings: { id: string; top: number }[] = []
      const pastHeadings: { id: string; top: number }[] = []

      headings.forEach((heading) => {
        const entry = headingElementsRef.current.get(heading.id)
        if (!entry) return

        const rect = entry.target.getBoundingClientRect()

        if (entry.isIntersecting) {
          visibleHeadings.push({ id: heading.id, top: rect.top })
        } else if (rect.top < 0) {
          // Heading has scrolled past the top
          pastHeadings.push({ id: heading.id, top: rect.top })
        }
      })

      let newActiveId: string | undefined

      if (visibleHeadings.length > 0) {
        // Sort by position and take the topmost visible
        visibleHeadings.sort((a, b) => a.top - b.top)
        newActiveId = visibleHeadings[0].id
      } else if (pastHeadings.length > 0) {
        // No visible headings, take the one closest to viewport top (most recently scrolled past)
        pastHeadings.sort((a, b) => b.top - a.top)
        newActiveId = pastHeadings[0].id
      }

      // Use ref to avoid stale closure - only update if different
      if (newActiveId && newActiveId !== activeIdRef.current) {
        setActiveId(newActiveId)
      }
    }

    // Create observer with rootMargin to detect headings near viewport top
    observerRef.current = new IntersectionObserver(callback, {
      // Observe from 100px above viewport to 40% below
      // This helps detect headings as they approach the top
      rootMargin: '-10% 0px -40% 0px',
      threshold: [0, 0.5, 1],
    })

    // Observe all heading elements
    headings.forEach((heading) => {
      const element = document.getElementById(heading.id)
      if (element) {
        observerRef.current?.observe(element)
      }
    })

    return () => {
      observerRef.current?.disconnect()
    }
  }, [headings])

  const handleClick = (id: string) => {
    // Immediately set as active to provide visual feedback
    setActiveId(id)

    const element = document.getElementById(id)
    if (element) {
      // Use scrollIntoView - works with CSS scroll-margin-top on headings
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })

      // Delay hash update to after scroll starts to prevent router interference
      requestAnimationFrame(() => {
        window.history.replaceState(null, '', `#${id}`)
      })
    }
  }

  const hasTocContent = headings.length > 0
  const hasAuthor = !!author

  if (!hasTocContent && !hasAuthor) return null

  return (
    <div className={cn('space-y-2', className)}>
      {hasTocContent && (
        <>
          <h4 className="text-sm font-semibold text-card-foreground mb-4">On this page</h4>
          <nav>
            <ul className="space-y-2">
              {headings.map((heading) => (
                <li key={heading.id}>
                  <button
                    type="button"
                    onClick={() => handleClick(heading.id)}
                    className={cn(
                      'block w-full text-left text-sm transition-colors hover:text-card-foreground/90 text-muted-foreground cursor-pointer',
                      activeId === heading.id && 'text-foreground'
                    )}
                  >
                    {heading.text}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </>
      )}

      {hasAuthor && (
        <div
          className={cn(
            'pt-4',
            hasTocContent && 'mt-6 border-t border-border/50'
          )}
        >
          <div className="flex items-center gap-3">
            {author.image && (
              <img
                src={author.image}
                alt={author.name}
                width={36}
                height={36}
                className="rounded-full object-cover"
              />
            )}
            <div>
              <p className="text-sm font-base text-card-foreground">{author.name}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
